// Exam utilities for mock RAE exam functionality

// Import all chapter and regulation data
const importAllChapters = () => {
  const chapters = {};
  for (let i = 1; i <= 32; i++) {
    const chapterNum = i.toString().padStart(2, '0');
    try {
      chapters[`chapter-${chapterNum}`] = require(`../chapters/chapter-${chapterNum}.json`);
    } catch (error) {
      // Skip missing chapters
    }
  }
  return chapters;
};

const importAllRegulations = () => {
  const regulations = {};
  const regulationIds = [
    'regulation-01-preliminary-definitions',
    'regulation-02-licensing-procedures',
    'regulation-03-license-terms-conditions',
    'regulation-04-amateur-station-operations',
    'regulation-05-general-provisions',
    'regulation-06-rights-liability-penalties',
    'regulation-07-frequency-allocations-call-signs'
  ];
  
  regulationIds.forEach(id => {
    try {
      regulations[id] = require(`../regulations/${id}.json`);
    } catch (error) {
      // Skip missing regulations
    }
  });
  
  return regulations;
};

// Shuffle array using Fisher-Yates algorithm
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// Get all questions from a content item (chapter or regulation)
const extractQuestions = (content, sourceId, sourceTitle, isRegulation = false) => {
  if (!content || !content.quiz || !content.quiz.questions) {
    return [];
  }
  
  return content.quiz.questions.map(question => ({
    ...question,
    sourceId,
    sourceTitle,
    isRegulation,
    section: isRegulation ? 'regulations' : 'technical'
  }));
};

// Select random questions for the exam
export const generateExamQuestions = () => {
  const chapters = importAllChapters();
  const regulations = importAllRegulations();
  
  // Collect all regulation questions
  let regulationQuestions = [];
  Object.entries(regulations).forEach(([id, regulation]) => {
    const questions = extractQuestions(regulation, id, regulation.title, true);
    regulationQuestions = regulationQuestions.concat(questions);
  });
  
  // Collect all technical questions
  let technicalQuestions = [];
  Object.entries(chapters).forEach(([id, chapter]) => {
    const questions = extractQuestions(chapter, id, chapter.title, false);
    technicalQuestions = technicalQuestions.concat(questions);
  });
  
  // Shuffle and select questions
  const shuffledRegulations = shuffleArray(regulationQuestions);
  const shuffledTechnical = shuffleArray(technicalQuestions);
  
  // Select 40 regulation questions and 60 technical questions
  const selectedRegulations = shuffledRegulations.slice(0, 40);
  const selectedTechnical = shuffledTechnical.slice(0, 60);
  
  // Combine with regulations first (questions 1-40), then technical (questions 41-100)
  // Give each question a unique exam ID to prevent answer collision
  const examQuestions = [
    ...selectedRegulations.map((q, index) => ({ 
      ...q, 
      examQuestionNumber: index + 1,
      examId: `exam-${index + 1}` // Unique ID for this exam instance
    })),
    ...selectedTechnical.map((q, index) => ({ 
      ...q, 
      examQuestionNumber: index + 41,
      examId: `exam-${index + 41}` // Unique ID for this exam instance
    }))
  ];
  
  return examQuestions;
};

// Calculate exam results
export const calculateExamResults = (questions, selectedAnswers) => {
  let regulationsCorrect = 0;
  let technicalCorrect = 0;
  let totalCorrect = 0;
  
  const questionResults = questions.map(question => {
    const userAnswer = selectedAnswers[question.examId];
    const isCorrect = userAnswer !== undefined && userAnswer === question.correct;
    
    if (isCorrect) {
      totalCorrect++;
      if (question.isRegulation) {
        regulationsCorrect++;
      } else {
        technicalCorrect++;
      }
    }
    
    return {
      ...question,
      userAnswer,
      isCorrect,
      wasAnswered: userAnswer !== undefined
    };
  });
  
  // Calculate percentages
  const regulationsPercentage = Math.round((regulationsCorrect / 40) * 100);
  const technicalPercentage = Math.round((technicalCorrect / 60) * 100);
  const overallPercentage = Math.round((totalCorrect / 100) * 100);
  
  // Determine pass/fail
  const regulationsPass = regulationsPercentage >= 50;
  const technicalPass = technicalPercentage >= 50;
  const overallPass = overallPercentage >= 65;
  const examPassed = regulationsPass && technicalPass && overallPass;
  
  return {
    questionResults,
    scores: {
      regulations: {
        correct: regulationsCorrect,
        total: 40,
        percentage: regulationsPercentage,
        passed: regulationsPass
      },
      technical: {
        correct: technicalCorrect,
        total: 60,
        percentage: technicalPercentage,
        passed: technicalPass
      },
      overall: {
        correct: totalCorrect,
        total: 100,
        percentage: overallPercentage,
        passed: overallPass
      }
    },
    examPassed
  };
};

// Group wrong answers by source for study recommendations
export const getStudyRecommendations = (questionResults) => {
  const wrongAnswers = questionResults.filter(q => !q.isCorrect && q.wasAnswered);
  
  const recommendations = {};
  
  wrongAnswers.forEach(question => {
    if (!recommendations[question.sourceId]) {
      recommendations[question.sourceId] = {
        sourceId: question.sourceId,
        sourceTitle: question.sourceTitle,
        isRegulation: question.isRegulation,
        wrongQuestions: []
      };
    }
    recommendations[question.sourceId].wrongQuestions.push(question);
  });
  
  return Object.values(recommendations).sort((a, b) => 
    b.wrongQuestions.length - a.wrongQuestions.length
  );
};

export default {
  generateExamQuestions,
  calculateExamResults,
  getStudyRecommendations
};